/** Describes a change in position and size */
export interface ITransform {
	/* Horizontal offset (after the scale) */
	readonly x: number;

	/* Vertical offset (after the scale) */
	readonly y: number;

	/* Uniform scale factor */
	readonly scale: number;
}

// Used functions instead of a class for better tree-shaking
// -----------------------------------------------------------

/** Calculate a transform to center and scale a size or rectangle in another rectangle */
export function fitInRectTransform(
	rectToFit: eXpress.math.IVector | eXpress.math.IRect,
	fitInRect: eXpress.math.IRect,
) {
	const childRatio = rectToFit.width / rectToFit.height;
	const containerRatio = fitInRect.width / fitInRect.height;

	let scale = 1;
	let x = 0;
	let y = 0;

	const top = (rectToFit as eXpress.math.IRect).top || 0;
	const left = (rectToFit as eXpress.math.IRect).left || 0;

	if (childRatio > containerRatio) {
		const childScaledHeight = fitInRect.width / childRatio;
		scale = fitInRect.width / rectToFit.width;
		x = fitInRect.left + -left * scale;
		y = fitInRect.top + (fitInRect.height - childScaledHeight) / 2 - top * scale;
	} else {
		const childScaledWidth = fitInRect.height * childRatio;
		scale = fitInRect.height / rectToFit.height;
		x = fitInRect.left + (fitInRect.width - childScaledWidth) / 2 - left * scale;
		y = fitInRect.top + -top * scale;
	}

	return { x, y, scale };
}

/** Convert a transform to CSS transform: translate + scale */
function transformToCssTransform(transform: ITransform) {
	return `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`;
}

export const unitTransform = {
	x: 0,
	y: 0,
	scale: 1,
} as const;

export function multiplyTransform(transform1: ITransform, transform2: ITransform): ITransform {
	return {
		x: transform2.x + transform1.x * transform2.scale,
		y: transform2.y + transform1.y * transform2.scale,
		scale: transform1.scale * transform2.scale,
	};
}

export function multiplyTransforms(transforms: ITransform[]): ITransform {
	if (transforms.length === 0) return unitTransform;
	if (transforms.length === 1) return transforms[0];
	if (transforms.length === 2) return multiplyTransform(transforms[0], transforms[1]);
	return transforms.reduce((agg, next) => multiplyTransform(agg, next));
}

export function isSimilarTransform(
	transform1: ITransform,
	transform2: ITransform,
	epsilon?: number,
): boolean {
	return (
		eXpress.math.areSimilar(transform1.x, transform2.x, epsilon) &&
		eXpress.math.areSimilar(transform1.y, transform2.y, epsilon) &&
		eXpress.math.areSimilar(transform1.scale, transform2.scale, epsilon)
	);
}

/**
 * Create a transform and fill missing properties with defaults
 * Useful when this object is later extended
 * @public
 */
export function createTransform({ x = 0, y = 0, scale = 1 }: Partial<ITransform>) {
	return {
		x,
		y,
		scale,
	};
}

/**
 * Get the point in a transformed world
 * @public
 */
export function pointInTransform(point: eXpress.math.IPoint, transform: ITransform) {
	return eXpress.math.point(
		(point.left - transform.x) / transform.scale,
		(point.top - transform.y) / transform.scale,
	);
}

/** Apply transformation on a point */
export function transformPoint(point: eXpress.math.IPoint, transform: ITransform) {
	// Equivalent for point.scale().offset(), but faster
	return eXpress.math.point(
		point.left * transform.scale + transform.x,
		point.top * transform.scale + transform.y,
	);
}

/** Apply transformation on a rect */
export function transformRect(rect: eXpress.math.IRect, transform: ITransform) {
	// Equivalent for rect.scale().offset(), but faster
	return eXpress.math.rect(
		rect.left * transform.scale + transform.x,
		rect.top * transform.scale + transform.y,
		rect.width * transform.scale,
		rect.height * transform.scale,
	);
}

/** Get the inverse of a transform, with the result you can go back to the original value */
export function invertTransform(transform: ITransform) {
	return {
		x: -transform.x / transform.scale,
		y: -transform.y / transform.scale,
		scale: 1 / transform.scale,
	};
}

/** Convert a transform to a frame */
export function transformToFrame(transform: ITransform) {
	return eXpress.math.scale(transform.scale).translate(transform.x, transform.y);
}

// Added a class for a more fluent interface
/** A combination of and offset and an uniform scale */
export class Transform implements ITransform {
	public static readonly unit = new Transform(unitTransform);
	public static readonly isSimilar = isSimilarTransform;

	public static mul(...transforms: ITransform[]) {
		return new Transform(multiplyTransforms(transforms));
	}

	public static invert(transform: ITransform) {
		return new Transform(invertTransform(transform));
	}

	public readonly x: number;
	public readonly y: number;
	public readonly scale: number;

	constructor({ x, y, scale }: Partial<ITransform>) {
		this.x = x ?? 0;
		this.y = y ?? 0;
		this.scale = scale ?? 1;
	}

	public mul(...transforms: ITransform[]) {
		return new Transform(multiplyTransforms([this, ...transforms]));
	}

	public inverse() {
		return new Transform(invertTransform(this));
	}

	public transformPoint(point: eXpress.math.IPoint) {
		return transformPoint(point, this);
	}

	public transformRect(rect: eXpress.math.IRect) {
		return transformRect(rect, this);
	}

	public isSimilarTo(transform: ITransform, epsilon?: number) {
		return isSimilarTransform(this, transform, epsilon);
	}

	public setCssTransform(el: HTMLElement): this {
		el.style.transform = transformToCssTransform(this);
		return this;
	}

	public toString() {
		return `translate(x:${this.x}, y:${this.y}, scale:${this.scale})`;
	}
}
